import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Clock, Download, Share2, Printer } from "lucide-react";
import api from "../lib/api";
import { useClock, fmtTime, ClockToggle } from "../lib/clockFormat";
import { useIsMobile, defaultDay } from "../lib/useIsMobile";
import { DaySwitcher } from "../components/DaySwitcher";

const PALETTE = ["#FCA5A5", "#86EFAC", "#FDE047", "#D8B4FE", "#7DD3FC", "#A7E4D3", "#FDBA74", "#C7D2FE"];
const TYPE_STYLE = { break: "#FDE68A88", lunch: "#F9A8D488", free: "#C7D2FE88" };

const subjectColor = (name) => {
  let h = 0;
  for (const ch of name || "") h = (h * 31 + ch.charCodeAt(0)) % 997;
  return PALETTE[h % PALETTE.length];
};

export default function PublicTimetable() {
  const { token } = useParams();
  const { pathname } = useLocation();
  const isFamily = pathname.startsWith("/k/");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isIOS] = useState(() => /iphone|ipad|ipod/i.test(navigator.userAgent));
  const [week, setWeek] = useState("A");
  const h24 = useClock();
  const isMobile = useIsMobile();
  const [mobileDay, setMobileDay] = useState("");

  useEffect(() => {
    document.title = "My Timetable — MyTimetable.co.uk";
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "theme-color";
      document.head.appendChild(meta);
    }
    meta.content = "#A7E4D3";
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    const original = link.getAttribute("href");
    link.setAttribute("href", `/api/pwa/manifest.json?start=${encodeURIComponent(window.location.pathname)}`);
    return () => { if (original) link.setAttribute("href", original); };
  }, []);

  useEffect(() => {
    api.get(isFamily ? `/parent/public/${token}` : `/public/timetable/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [token, isFamily]);

  const install = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="public-timetable-error">
        <div className="nb-card p-10 max-w-md w-full text-center bg-white">
          <h1 className="font-display font-black text-2xl">Link not available</h1>
          <p className="text-zinc-500 text-sm mt-2">This timetable link has been turned off or never existed. Ask the pupil to share a fresh link from their MyTimetable.co.uk app.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center font-display font-black text-2xl animate-pulse" data-testid="public-timetable-loading">
        Loading timetable…
      </div>
    );
  }

  const { days, slots } = data.schedule;
  const weeks = data.schedule.weeks || 1;
  const slotKey = (day, idx) => (weeks === 2 ? `${week}:${day}:${idx}` : `${day}:${idx}`);
  const dayOverrides = data.schedule.day_overrides || {};
  const daySlotsList = days.map((d) => dayOverrides[d]?.slots || slots);
  const rowCount = Math.max(slots.length, ...daySlotsList.map((s) => s.length));
  const activeDay = days.includes(mobileDay) ? mobileDay : defaultDay(days);
  const shownDays = isMobile ? [activeDay] : days;

  return (
    <div className="min-h-screen pb-24" data-testid="public-timetable-page">
      <header className="border-b-2 border-zinc-900 bg-[#A7E4D3] sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 shrink-0 rounded-xl border-2 border-zinc-900 bg-white flex items-center justify-center shadow-[3px_3px_0px_0px_#18181B]">
              <Clock className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-black text-lg leading-tight truncate" data-testid="public-pupil-name">{data.pupil_name}'s timetable</div>
              <div className="text-xs font-bold text-zinc-700 truncate">{data.school_name} · {data.year_group}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 no-print">
            <ClockToggle className="nb-btn bg-white px-3 py-2 text-sm font-bold" />
            <button onClick={() => window.print()} data-testid="public-print-btn" className="nb-btn bg-white px-3 py-2 text-sm">
              <Printer className="w-4 h-4" /> <span className="hidden sm:inline">Print</span>
            </button>
            {installPrompt && (
              <button onClick={install} data-testid="pwa-install-btn" className="nb-btn bg-white px-3 py-2 text-sm">
                <Download className="w-4 h-4" /> Install
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-3 sm:px-4 py-6">
        {(data.messages || []).length > 0 && (
          <div className="nb-card p-4 mb-6 bg-[#FDE68A]" data-testid="public-messages">
            <div className="font-display font-bold text-sm mb-2">Notes from home</div>
            {data.messages.slice().reverse().map((m) => (
              <p key={m.id} className="text-sm font-semibold py-1 border-b border-zinc-900/10 last:border-b-0" data-testid={`public-message-${m.id}`}>{m.text}</p>
            ))}
          </div>
        )}
        {(data.notices || []).length > 0 && (
          <div className="nb-card p-4 mb-6 bg-[#D8B4FE]" data-testid="public-notices">
            <div className="font-display font-bold text-sm mb-2">School notices</div>
            {data.notices.map((n, i) => (
              <div key={i} className="py-1.5 border-b border-zinc-900/10 last:border-b-0" data-testid={`public-notice-${i}`}>
                <div className="font-bold text-sm">{n.title}</div>
                <p className="text-sm">{n.body}</p>
              </div>
            ))}
          </div>
        )}
        <div className="hidden print:block font-display font-black text-xl mb-3">{data.pupil_name} — {data.school_name}{weeks === 2 ? ` · Week ${week}` : ""}</div>
        {weeks === 2 && (
          <div className="flex gap-2 mb-4 no-print" data-testid="public-week-tabs">
            {["A", "B"].map((w) => (
              <button key={w} onClick={() => setWeek(w)} data-testid={`public-week-tab-${w}`}
                className={`nb-btn px-5 py-2 text-sm ${week === w ? "bg-[#1E3A8A] text-white" : "bg-white text-zinc-600"}`}>
                Week {w}
              </button>
            ))}
          </div>
        )}
        {isMobile && <DaySwitcher days={days} active={activeDay} onChange={setMobileDay} testPrefix="public" />}
        <div className="nb-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse ${isMobile ? "" : "min-w-[560px]"}`} data-testid="public-timetable-grid">
              <thead>
                <tr>
                  <th className="border-b-2 border-r-2 border-zinc-900 bg-zinc-900 text-white text-xs font-bold p-2.5 text-left w-24 sticky left-0">Time</th>
                  {shownDays.map((d) => (
                    <th key={d} className="border-b-2 border-zinc-900 bg-zinc-900 text-white p-2.5 font-bold text-sm">{d.slice(0, 3)}<span className="hidden sm:inline">{d.slice(3)}</span></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: rowCount }).map((_, idx) => {
                  const slot = slots[idx];
                  return (
                  <tr key={idx}>
                    <td className="border-r-2 border-b-2 border-zinc-900 bg-white p-2.5 sticky left-0 z-10">
                      <div className="font-bold text-sm">{slot ? slot.label : "—"}</div>
                      <div className="text-[11px] text-zinc-500 font-semibold">{slot ? `${fmtTime(slot.start, h24)}–${fmtTime(slot.end, h24)}` : ""}</div>
                    </td>
                    {shownDays.map((day) => {
                      const dSlot = (dayOverrides[day]?.slots || slots)[idx];
                      if (!dSlot) {
                        return <td key={day} className="border-b-2 border-zinc-300 p-1.5 bg-zinc-100" data-testid={`public-slot-${day.toLowerCase()}-${idx}`} />;
                      }
                      if (dSlot.type !== "period") {
                        return (
                          <td key={day} className="border-b-2 border-zinc-300 p-1.5 text-center text-xs font-bold text-zinc-500"
                            style={{ backgroundColor: TYPE_STYLE[dSlot.type] }} data-testid={`public-slot-${day.toLowerCase()}-${idx}`}>
                            {dSlot.label}
                          </td>
                        );
                      }
                      const entry = data.entries[slotKey(day, idx)];
                      return (
                        <td key={day} className="border-b-2 border-zinc-300 p-1.5" data-testid={`public-slot-${day.toLowerCase()}-${idx}`}>
                          {dayOverrides[day] && <div className="text-[9px] font-black text-zinc-400 mb-1 px-0.5">{dSlot.label} · {fmtTime(dSlot.start, h24)}–{fmtTime(dSlot.end, h24)}</div>}
                          {entry ? (
                            <div className="min-h-[56px] rounded-lg border-2 border-zinc-900 p-2 shadow-[2px_2px_0px_0px_#18181B]"
                              style={{ backgroundColor: subjectColor(entry.subject) }}>
                              <div className="font-display font-bold text-sm leading-tight">{entry.subject || "Class"}</div>
                              <div className="text-[11px] font-semibold text-zinc-700 mt-0.5 leading-tight">
                                {[entry.teacher, entry.room].filter(Boolean).join(" · ")}
                              </div>
                            </div>
                          ) : (
                            <div className="min-h-[56px] rounded-lg border-2 border-dashed border-zinc-200 bg-zinc-50" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {isIOS && (
          <div className="nb-card p-5 mt-6 bg-[#FDE68A] no-print" data-testid="ios-install-hint">
            <h2 className="font-display font-bold flex items-center gap-2"><Share2 className="w-5 h-5" /> Save to your home screen</h2>
            <p className="text-sm mt-2 font-semibold text-zinc-700">Tap the Share button in Safari, then "Add to Home Screen" — your timetable will open like an app, even offline.</p>
          </div>
        )}

        <p className="text-center text-xs text-zinc-400 font-semibold mt-8">
          Powered by <Link to="/" className="underline font-bold text-zinc-600" data-testid="public-powered-by">MyTimetable.co.uk</Link> — timetables for schools & families
        </p>
      </main>
    </div>
  );
}
